import { formatNumber } from "sipli-fleet-core/utils/formatNumber";
import { App, Drawer, Form } from "antd";
import type { TFunction } from "i18next";
import type { ComponentType, ReactNode } from "react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import DeleteIcon from "sipli-fleet-core/assets/icons/delete-icon.svg";
import EditIcon from "sipli-fleet-core/assets/icons/edit-icon.svg";
import { CustomButton, CustomTooltip, CustomTypography } from "sipli-fleet-core/components";
import { MAPPING_DRAWER_CROSS_ICON } from "src/assets/svgs";
import {
  useMutationDeleteDeviceMapping,
  useMutationUpdateDeviceMapping,
  useQueryGetRelayStatusLookups,
  useQueryGetSwitchOnOffStateTypeLookups,
  useQueryGetTemperatureTypeLookups,
} from "src/queries/mapping.queries";
import {
  useMutationDeleteMappingPresetMapping,
  useMutationUpdateMappingPresetMapping,
} from "src/queries/mappingPreset.queries";
import {
  applyFilterPreview,
  buildMappingSubmitPayload,
  deleteMappingWithFeedback,
  getIncomingOptions,
  getIncomingOptionForMapping,
  getInitialMappingValues,
  getIfStatementOperatorLabel,
  getMappingFilters,
  getMappingName,
  getObjectById,
  getOptionsByKey,
  getSwitchStateStaticOptions,
  makeFilter,
  normalizeFormPrimitive,
  toLookupOptions,
  transformMappingDrawerData,
} from "./mapping.helpers";

import type { IncomingValuesResponse } from "src/services/mappingService";
import type { MappingAddEditValues, MappingFilterRecord, MappingRecord } from "./mapping.types";
import MappingDelete from "./MappingDelete";
import { MappingEditorFields } from "./MappingEditorFields";
import { cn } from "../../../../../../sipli-fleet-core/utils/cn";

interface MappingDetailsContentProps {
  mappings: MappingRecord[];
  availabilityMappings?: MappingRecord[];
  parentKey?: string;
  childKey?: string;
  showHeading?: boolean;
  framed?: boolean;
  unitId?: string | number;
  incomingValues?: IncomingValuesResponse;
  mode?: "device" | "preset";
  presetId?: string | number;
  deviceModelId?: string | number;
  readOnly?: boolean;
  onDeleteSuccess?: () => void;
}

const EditIconComponent = EditIcon as unknown as ComponentType<{
  className?: string;
}>;

const DeleteIconComponent = DeleteIcon as unknown as ComponentType<{
  className?: string;
}>;

const MappingDetailsContent = ({
  mappings,
  availabilityMappings = mappings,
  parentKey,
  childKey,
  showHeading = true,
  framed = true,
  unitId,
  incomingValues,
  mode = "device",
  presetId,
  deviceModelId,
  readOnly,
  onDeleteSuccess,
}: MappingDetailsContentProps) => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const [editingMapping, setEditingMapping] = useState<MappingRecord | null>(null);
  const [deleteRecord, setDeleteRecord] = useState<MappingRecord | null>(null);
  const deleteMutation = useMutationDeleteDeviceMapping();
  const deletePresetMappingMutation = useMutationDeleteMappingPresetMapping();
  const isPresetMode = mode === "preset";

  const handleDelete = async () => {
    if (!deleteRecord || !unitId) return;

    if (isPresetMode && presetId) {
      try {
        await deletePresetMappingMutation.mutateAsync({
          mappingId: deleteRecord.id,
          presetId,
        });
        message.success(
          t("preset_mapping_deleted_successfully", {
            defaultValue: "Preset Mapping Deleted Successfully!",
          })
        );
        setDeleteRecord(null);
        onDeleteSuccess?.();
        if (editingMapping?.id === deleteRecord.id) setEditingMapping(null);
      } catch {
        message.error(t("error"));
      }
      return;
    }

    await deleteMappingWithFeedback({
      mapping: deleteRecord,
      unitId,
      deleteMapping: deleteMutation.mutateAsync,
      message,
      t,
      onSuccess: () => {
        setDeleteRecord(null);
        onDeleteSuccess?.();
        if (editingMapping?.id === deleteRecord.id) setEditingMapping(null);
      },
    });
  };

  return (
    <div className="w-full">
      {showHeading && (
        <>
          {parentKey && (
            <div className="bg-primary-shade px-[16px] py-[8px] text-[19.93px] font-bold leading-[28px] text-white">
              {parentKey}
            </div>
          )}
          {childKey && (
            <MappingChildCard
              title={childKey}
              mappings={mappings}
              availabilityMappings={availabilityMappings}
              incomingValueId={childKey}
              unitId={unitId}
              incomingValues={incomingValues}
              mode={mode}
              presetId={presetId}
              deviceModelId={deviceModelId}
              readOnly={readOnly}
              editingMapping={editingMapping}
              onEditMapping={setEditingMapping}
              onCancelEdit={() => setEditingMapping(null)}
              onSavedEdit={() => {
                setEditingMapping(null);
                onDeleteSuccess?.();
              }}
              onDeleteMapping={setDeleteRecord}
            />
          )}
        </>
      )}

      {!showHeading || !childKey ? (
        <div
          className={
            framed ? "rounded-[8px] border border-[#3369F4] bg-white p-[8px]" : "space-y-[16px]"
          }
        >
          {mappings.length ? (
            mappings.map((mapping) => (
              <MappingDetailsItem
                key={mapping.id}
                mapping={mapping}
                availabilityMappings={availabilityMappings}
                incomingValueId={childKey}
                unitId={unitId}
                incomingValues={incomingValues}
                mode={mode}
                presetId={presetId}
                deviceModelId={deviceModelId}
                readOnly={readOnly}
                isEditing={editingMapping?.id === mapping.id}
                onEditMapping={setEditingMapping}
                onCancelEdit={() => setEditingMapping(null)}
                onSavedEdit={() => {
                  setEditingMapping(null);
                  onDeleteSuccess?.();
                }}
                onDeleteMapping={setDeleteRecord}
              />
            ))
          ) : (
            <div className="py-4 text-center text-[15px] font-medium leading-[22px] text-gray9">
              {t("no_data")}
            </div>
          )}
        </div>
      ) : null}
      <MappingDelete
        open={Boolean(deleteRecord)}
        isLoading={deleteMutation.isPending || deletePresetMappingMutation.isPending}
        onCancel={() => setDeleteRecord(null)}
        onConfirm={handleDelete}
      />
    </div>
  );
};

interface MappingChildCardProps {
  title: string;
  mappings: MappingRecord[];
  availabilityMappings?: MappingRecord[];
  incomingValueId?: string;
  unitId?: string | number;
  incomingValues?: IncomingValuesResponse;
  mode?: "device" | "preset";
  presetId?: string | number;
  deviceModelId?: string | number;
  readOnly?: boolean;
  editingMapping?: MappingRecord | null;
  onEditMapping: (mapping: MappingRecord) => void;
  onCancelEdit: () => void;
  onSavedEdit: () => void;
  onDeleteMapping: (mapping: MappingRecord) => void;
}

const MappingChildCard = ({
  title,
  mappings,
  availabilityMappings = mappings,
  incomingValueId,
  unitId,
  incomingValues,
  mode,
  presetId,
  deviceModelId,
  readOnly,
  editingMapping,
  onEditMapping,
  onCancelEdit,
  onSavedEdit,
  onDeleteMapping,
}: MappingChildCardProps) => (
  <div className="m-[18px] rounded-[16px] border-2 border-[#3369F4] bg-[#F5F5F9] px-[14px] pt-[18px] pb-[10px]">
    <CustomTypography.CustomText className="mb-[15px] block break-words !text-[17.72px] !font-bold !leading-[28px] !text-gray10">
      {title}
    </CustomTypography.CustomText>
    <div className="space-y-[16px]">
      {mappings.map((mapping) => (
        <MappingDetailsItem
          key={mapping.id}
          mapping={mapping}
          siblingMappings={mappings}
          availabilityMappings={availabilityMappings}
          incomingValueId={incomingValueId}
          unitId={unitId}
          incomingValues={incomingValues}
          mode={mode}
          presetId={presetId}
          deviceModelId={deviceModelId}
          readOnly={readOnly}
          isEditing={editingMapping?.id === mapping.id}
          onEditMapping={onEditMapping}
          onCancelEdit={onCancelEdit}
          onSavedEdit={onSavedEdit}
          onDeleteMapping={onDeleteMapping}
        />
      ))}
    </div>
  </div>
);

interface MappingDetailsItemProps {
  mapping: MappingRecord;
  siblingMappings?: MappingRecord[];
  availabilityMappings?: MappingRecord[];
  incomingValueId?: string;
  unitId?: string | number;
  incomingValues?: IncomingValuesResponse;
  mode?: "device" | "preset";
  presetId?: string | number;
  deviceModelId?: string | number;
  readOnly?: boolean;
  isEditing?: boolean;
  onEditMapping: (mapping: MappingRecord) => void;
  onCancelEdit: () => void;
  onSavedEdit: () => void;
  onDeleteMapping: (mapping: MappingRecord) => void;
}

const MappingDetailsItem = ({
  mapping,
  siblingMappings = [],
  availabilityMappings = siblingMappings,
  incomingValueId,
  unitId,
  incomingValues,
  mode = "device",
  presetId,
  deviceModelId,
  readOnly,
  isEditing,
  onEditMapping,
  onCancelEdit,
  onSavedEdit,
  onDeleteMapping,
}: MappingDetailsItemProps) => {
  const { t } = useTranslation();
  const mappingFilters = getMappingFilters(mapping);
  const hasData =
    Boolean(mappingFilters.length) ||
    Object.keys(mapping.mappingFields ?? {}).length > 0 ||
    Boolean(mapping.forwardTo);

  const getFieldOptions = (key: string) =>
    key === "switchStateFieldType" ? getSwitchStateStaticOptions(t) : getOptionsByKey(key);

  return (
    <div className="overflow-hidden rounded-[4px] border border-[#3369F4] bg-white">
      <div className="flex h-[48px] items-center justify-between gap-3 border-b border-[#3369F4] bg-[#EBF0FB] px-[10px] py-[12px]">
        <CustomTypography.CustomText className="min-w-0 break-words !text-[15.75px] !font-semibold !leading-[20px] !text-black">
          {getMappingName(mapping.mappingTypeId, t)}
        </CustomTypography.CustomText>
        {!isEditing && (
          <div className="flex shrink-0 items-center gap-1">
            <CustomTooltip title={readOnly ? t("base_presets_cannot_be_changed") : t("edit")}>
              <CustomButton
                type="icon"
                buttonSize="small"
                aria-label={t("edit")}
                className={cn("!cursor-pointer focus:!bg-transparent", {
                  "!cursor-not-allowed": readOnly,
                })}
                disabled={readOnly}
                icon={
                  <EditIconComponent
                    className={cn("w-[16px] h-[16px]", {
                      "opacity-30 grayscale": readOnly,
                    })}
                  />
                }
                onClick={(event) => {
                  event.stopPropagation();
                  onEditMapping(mapping);
                }}
              />
            </CustomTooltip>

            <CustomTooltip
              title={
                readOnly
                  ? t("base_presets_cannot_be_deleted", {
                      defaultValue: "Base presets cannot be deleted.",
                    })
                  : t("delete")
              }
            >
              <CustomButton
                type="icon"
                buttonSize="small"
                aria-label={t("delete")}
                className={cn("!cursor-pointer focus:!bg-transparent", {
                  "!cursor-not-allowed": readOnly,
                })}
                disabled={readOnly}
                icon={
                  <DeleteIconComponent
                    className={cn("w-[15px] h-[16px]", {
                      "opacity-30 grayscale": readOnly,
                    })}
                  />
                }
                onClick={(event) => {
                  event.stopPropagation();
                  onDeleteMapping(mapping);
                }}
              />
            </CustomTooltip>
          </div>
        )}
      </div>

      {isEditing && unitId && incomingValues ? (
        <MappingInlineEditor
          unitId={unitId}
          incomingValues={incomingValues}
          mapping={mapping}
          mappings={availabilityMappings}
          incomingValueId={incomingValueId}
          mode={mode}
          presetId={presetId}
          deviceModelId={deviceModelId}
          onCancel={onCancelEdit}
          onSaved={onSavedEdit}
        />
      ) : !hasData ? (
        <div className="px-[16px] py-[18px] text-center text-[15px] font-medium leading-[22px] text-gray9">
          {t("no_data")}
        </div>
      ) : (
        <div className="space-y-[16px] px-[16px] py-[18px]">
          {Object.keys(mapping.mappingFields ?? {}).length > 0 && (
            <MappingSection label={`${t("mapping_fields")}:`}>
              {Object.entries(mapping.mappingFields ?? {}).map(([key, value]) => {
                const option = getObjectById(getFieldOptions(key), value);
                const label = option?.label || String(value);

                return (
                  <MappingFieldRow
                    key={key}
                    label={getMappingFieldLabel(key, t)}
                    value={label}
                    numeric={!option && ["tankCapacity", "maxDecSpeed", "maxIncSpeed", "medianWidth"].includes(key)}
                  />
                );
              })}
            </MappingSection>
          )}

          {!!mappingFilters.length && (
            <MappingSection label={`${t("filters")}:`} emphasized>
              {mappingFilters.map((filter) => (
                <div key={`${filter.id}-${filter.uniqueId ?? ""}`} className="mb-[10px] last:mb-0">
                  <div className="break-words mb-2.5 text-[14px] capitalize leading-[18px] text-black">
                    {filter.id.replace(/_/g, " ")}:
                  </div>
                  {Object.entries(filter.fields ?? {}).map(([key, value]) => {
                    const displayValue =
                      key === "operator" ? getIfStatementOperatorLabel(value) : value;

                    return (
                      <MappingFieldRow
                        key={key}
                        label={key}
                        value={displayValue}
                        numeric={["offset", "multiplier", "param", "transformedValue", "minValue", "maxValue"].includes(key)}
                      />
                    );
                  })}
                </div>
              ))}
            </MappingSection>
          )}

          {mapping.forwardTo && (
            <div>
              <div className="mb-[6px] text-[18px] font-bold leading-[24px] text-[#3C3F4A]">
                {t("forward_target")}:
              </div>
              <MappingFieldRow label={t("forwardTo")} value={mapping.forwardTo} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

interface MappingInlineEditorProps {
  unitId: string | number;
  incomingValues: IncomingValuesResponse;
  mapping: MappingRecord;
  mappings?: MappingRecord[];
  incomingValueId?: string;
  mode?: "device" | "preset";
  presetId?: string | number;
  deviceModelId?: string | number;
  onCancel: () => void;
  onSaved: () => void;
}

const MappingInlineEditor = ({
  unitId,
  incomingValues,
  mapping,
  mappings = [],
  incomingValueId,
  mode = "device",
  presetId,
  deviceModelId,
  onCancel,
  onSaved,
}: MappingInlineEditorProps) => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const [form] = Form.useForm<MappingAddEditValues>();
  const updateMutation = useMutationUpdateDeviceMapping();
  const updatePresetMappingMutation = useMutationUpdateMappingPresetMapping();
  const relayLookupQuery = useQueryGetRelayStatusLookups(true);
  const switchLookupQuery = useQueryGetSwitchOnOffStateTypeLookups(true);
  const incomingOptions = useMemo(() => getIncomingOptions(incomingValues), [incomingValues]);
  const [filters, setFilters] = useState<MappingFilterRecord[]>([]);
  const [filterDeleteUniqueId, setFilterDeleteUniqueId] = useState<string>();
  const [formValues, setFormValues] = useState<MappingAddEditValues>({});
  const watchedMappingType = Form.useWatch("selectMapping", form);
  const watchedValueId = Form.useWatch("value", form);
  const watchedLinkedToFuel = Form.useWatch("linkedToFuel", form);
  const watchedTargetCheckbox = Form.useWatch("targetCheckbox", form);
  const watchedValues = Form.useWatch([], form) as MappingAddEditValues | undefined;
  const selectedIncomingOption = incomingOptions.find((item) => item.id === watchedValueId);
  const effectiveMappingType = watchedMappingType ?? mapping.mappingTypeId;
  const temperatureTypeLookupQuery = useQueryGetTemperatureTypeLookups(
    effectiveMappingType === "temperature"
  );

  useEffect(() => {
    const incomingOption = getIncomingOptionForMapping(incomingOptions, mapping, incomingValueId);
    const initialValues = getInitialMappingValues(mapping, incomingOption);
    setFilters(initialValues.filterType ?? []);
    setFormValues(initialValues);
    form.setFieldsValue(initialValues);
  }, [form, incomingOptions, incomingValueId, mapping]);

  useEffect(() => {
    if (!selectedIncomingOption) return;
    form.setFieldValue("lastValue", selectedIncomingOption.lastValue);
    setFormValues((prev) => ({
      ...prev,
      lastValue: selectedIncomingOption.lastValue,
    }));
  }, [form, selectedIncomingOption]);

  const syncFilters = (nextFilters: MappingFilterRecord[]) => {
    setFilters(nextFilters);
    form.setFieldValue("filterType", nextFilters);
    setFormValues((prev) => ({
      ...prev,
      filterType: nextFilters,
    }));
  };

  const handleAddFilter = (filterId: string) => {
    syncFilters([...filters, makeFilter(filterId)]);
  };

  const handleRemoveFilter = (uniqueId?: string) => {
    syncFilters(filters.filter((filter) => (filter.uniqueId ?? filter.id) !== uniqueId));
  };

  const handleConfirmRemoveFilter = () => {
    handleRemoveFilter(filterDeleteUniqueId);
    setFilterDeleteUniqueId(undefined);
  };

  const handleReorderFilters = (sourceIndex: number, destinationIndex: number) => {
    if (sourceIndex < 0 || destinationIndex < 0 || sourceIndex === destinationIndex) return;

    const nextFilters = [...filters];
    const [movedFilter] = nextFilters.splice(sourceIndex, 1);
    if (!movedFilter) return;

    nextFilters.splice(destinationIndex, 0, movedFilter);
    syncFilters(nextFilters);
  };

  const previewValue = useMemo(() => {
    const values = {
      ...formValues,
      ...form.getFieldsValue(),
      ...(watchedValues ?? {}),
    };
    let result = values.lastValue;

    filters.forEach((filter) => {
      result = normalizeFormPrimitive(applyFilterPreview(filter, result, values));
    });

    return result;
  }, [filters, form, formValues, watchedValues]);

  const handleSubmit = async () => {
    await form.validateFields();

    const allValues = form.getFieldsValue(true) as MappingAddEditValues;
    const values: MappingAddEditValues = {
      ...allValues,
      selectMapping: allValues.selectMapping ?? mapping.mappingTypeId,
      value: allValues.value ?? form.getFieldValue("value"),
      filterType: filters,
    };
    const { updatePayload } = buildMappingSubmitPayload(values, incomingOptions, unitId, mapping);

    try {
      if (mode === "preset" && presetId) {
        await updatePresetMappingMutation.mutateAsync({
          id: mapping.id,
          presetId,
          payload: {
            ...updatePayload,
            ...(deviceModelId !== undefined && deviceModelId !== null
              ? { deviceType: deviceModelId }
              : {}),
            isPreset: true,
          },
        });
        message.success(
          t("preset_mapping_updated_successfully", {
            defaultValue: "Preset Mapping Updated Successfully!",
          })
        );
      } else {
        await updateMutation.mutateAsync({
          id: mapping.id,
          unitId,
          payload: updatePayload,
        });
        message.success("Mapping Updated Successfully!");
      }
      onSaved();
    } catch {
      message.error(t("error"));
    }
  };

  return (
    <>
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        onValuesChange={(_, allValues) => setFormValues(allValues as MappingAddEditValues)}
      >
        <MappingEditorFields
          incomingOptions={incomingOptions}
          watchedFilters={filters}
          watchedMappingType={effectiveMappingType}
          watchedLinkedToFuel={Boolean(watchedLinkedToFuel)}
          watchedTargetCheckbox={Boolean(watchedTargetCheckbox)}
          previewValue={previewValue}
          relayOptions={toLookupOptions(relayLookupQuery.data)}
          switchOptions={
            toLookupOptions(switchLookupQuery.data).length
              ? toLookupOptions(switchLookupQuery.data)
              : getSwitchStateStaticOptions(t)
          }
          temperatureTypeOptions={toLookupOptions(temperatureTypeLookupQuery.data)}
          mappings={mappings}
          editingMapping={mapping}
          onAddFilter={handleAddFilter}
          onRemoveFilter={setFilterDeleteUniqueId}
          onReorderFilters={handleReorderFilters}
          variant="drawer"
        />
        <div className="mt-4 mb-2 mx-[14px] flex justify-end gap-3">
          <CustomButton
            label={t("cancel")}
            type="secondary"
            className="!h-[32px] !w-[144.5px]"
            onClick={onCancel}
          />
          <CustomButton
            label={t("save")}
            type="primary"
            className="!h-[32px] !w-[144.5px]"
            loading={updateMutation.isPending}
            onClick={handleSubmit}
          />
        </div>
      </Form>
      <MappingDelete
        open={Boolean(filterDeleteUniqueId)}
        isLoading={false}
        title={t("delete_filter", { defaultValue: "Delete Filter" })}
        onCancel={() => setFilterDeleteUniqueId(undefined)}
        onConfirm={handleConfirmRemoveFilter}
      />
    </>
  );
};

interface MappingSectionProps {
  label: string;
  children: ReactNode;
  emphasized?: boolean;
}

const MappingSection = ({ label, children, emphasized }: MappingSectionProps) => (
  <div className={emphasized ? "rounded-[8px] bg-[#F5F5FA] px-[18px] py-[16px]" : undefined}>
    <div className="mb-[8px] border-b border-[#D9DDE7] pb-[6px] text-[15.75px] font-semibold leading-[20px] text-gray10">
      {label}
    </div>
    <div>{children}</div>
  </div>
);

interface MappingFieldRowProps {
  label: string;
  numeric?: boolean;
  value: unknown;
}

const MappingFieldRow = ({ label, numeric = false, value }: MappingFieldRowProps) => {
  const { i18n } = useTranslation();
  return (
  <div className="mb-[8px] break-words text-[14px] leading-[18px] text-gray8 last:mb-0">
    <span className="capitalize text-black">{label}: </span>
    {numeric && (typeof value === "number" || typeof value === "string")
      ? formatNumber(value, i18n.language)
      : value === null || value === undefined || value === "" ? "--" : String(value)}
  </div>
  );
};

const mappingFieldLabelKeys: Record<string, string> = {
  axisSingleValue: "axle",
  canDataConvert: "convert_j1939",
  canDataFieldType: "type",
  card_presence_driver: "driver",
  disabledNotificationAlert: "disable_notification_alert",
  driver_id_driver: "driver",
  driver_id_format: "type",
  enableRefueling: "enable_refueling",
  field_Save_only_when_changes: "save_only_when_changes",
  field_Source: "source",
  field_tank_ID: "unique_id",
  field_Type: "type",
  fuelCatalystLevelPercentage: "convert_j1939",
  fuelLevePercentageCan: "convert_j1939",
  fuelTankIdField: "tank_id",
  fuel_consumed: "fuel_consumed",
  label: "label",
  linkedToFuel: "linked_to_fuel",
  maxDecSpeed: "max_dec_speed_l_min",
  maxIncSpeed: "max_inc_speed_l_min",
  medianWidth: "median_width",
  name_driver: "driver",
  readableValueName: "name",
  related_state_driver: "driver",
  relayId: "id",
  relayLabel: "relay_label",
  sendCommandWhileDriving: "allow_command_while_driving",
  surname_driver: "driver",
  switchStateFieldType: "type",
  switchStateUniqueId: "unique_id",
  switchStateValueOff: "value_off",
  tankCapacity: "tank_capacity_l",
  type: "type",
  unit: "unit",
  valueOff: "value_off",
  valueOffLabel: "off_label",
  valueOn: "value_on",
  valueOnLabel: "value_on",
  wheelSingleValue: "wheel",
  working_state_driver: "driver",
};

const getMappingFieldLabel = (key: string, t: TFunction) => {
  const labelKey = mappingFieldLabelKeys[key];
  return labelKey ? t(labelKey) : key.replace(/_/g, " ").replace(/([a-z])([A-Z])/g, "$1 $2");
};

const mappingDrawerStyles = {
  header: {
    padding: "18px 18px 15px",
    borderBottom: "1px solid #E5EAF5",
  },
  body: {
    padding: 0,
    background: "#FFFFFF",
  },
};

interface MappingDrawerTitleProps {
  title: string;
  onClose: () => void;
}

const MappingDrawerTitle = ({ title, onClose }: MappingDrawerTitleProps) => (
  <div className="w-full flex items-center justify-between gap-3">
    <CustomTypography.CustomText className="!text-[25.23px] !font-bold !leading-[28px] !text-black">
      {title}
    </CustomTypography.CustomText>
    <img
      src={MAPPING_DRAWER_CROSS_ICON}
      alt="Cross Icon"
      className="cursor-pointer"
      onClick={onClose}
    />
  </div>
);

interface MappingDetailsDrawerProps {
  open: boolean;
  onClose: () => void;
  mappings: MappingRecord[];
  availabilityMappings?: MappingRecord[];
  parentKey?: string;
  childKey?: string;
  unitId?: string | number;
  incomingValues: IncomingValuesResponse;
  mode?: "device" | "preset";
  presetId?: string | number;
  deviceModelId?: string | number;
  readOnly?: boolean;
  drawerWidth?: number | string;
}

export const MappingDetailsDrawer = ({
  open,
  onClose,
  mappings,
  availabilityMappings,
  childKey,
  unitId,
  incomingValues,
  mode,
  presetId,
  deviceModelId,
  readOnly,
  drawerWidth = 430,
}: MappingDetailsDrawerProps) => {
  const { t } = useTranslation();

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={drawerWidth}
      title={
        <MappingDrawerTitle
          title={t("mappingDetails", { defaultValue: "Mapping Details" })}
          onClose={onClose}
        />
      }
      closable={false}
      styles={mappingDrawerStyles}
    >
      <MappingDetailsContent
        mappings={mappings}
        availabilityMappings={availabilityMappings}
        childKey={childKey}
        unitId={unitId}
        incomingValues={incomingValues}
        mode={mode}
        presetId={presetId}
        deviceModelId={deviceModelId}
        readOnly={readOnly}
        onDeleteSuccess={onClose}
      />
    </Drawer>
  );
};

interface AllMappingsDrawerProps {
  open: boolean;
  onClose: () => void;
  unitId?: string | number;
  incomingValues: IncomingValuesResponse;
  mappings: MappingRecord[];
  mode?: "device" | "preset";
  presetId?: string | number;
  deviceModelId?: string | number;
  readOnly?: boolean;
  drawerWidth?: number | string;
}

export const AllMappingsDrawer = ({
  open,
  onClose,
  unitId,
  incomingValues,
  mappings,
  mode,
  presetId,
  deviceModelId,
  readOnly,
  drawerWidth = 430,
}: AllMappingsDrawerProps) => {
  const { t } = useTranslation();
  const data = transformMappingDrawerData(incomingValues, mappings);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={drawerWidth}
      title={
        <MappingDrawerTitle
          title={t("mappingDetails", { defaultValue: "Mapping Details" })}
          onClose={onClose}
        />
      }
      closable={false}
      styles={mappingDrawerStyles}
    >
      {data.map((parent) => (
        <div key={parent.parentName}>
          <div className="bg-primary-shade px-[16px] py-[8px] text-[19.93px] font-bold leading-[28px] text-white">
            {parent.parentName}
          </div>
          {parent.children.map((child) => (
            <MappingDetailsContent
              key={`${parent.parentName}-${child.name}`}
              mappings={child.data}
              availabilityMappings={mappings}
              childKey={child.name}
              showHeading
              unitId={unitId}
              incomingValues={incomingValues}
              mode={mode}
              presetId={presetId}
              deviceModelId={deviceModelId}
              readOnly={readOnly}
              onDeleteSuccess={onClose}
            />
          ))}
        </div>
      ))}
    </Drawer>
  );
};

export default MappingDetailsContent;
